import * as IoC from 'typescript-ioc';
import { IApiClient, RequestContentType } from '../../infrastructure/api-client';
import * as URL from '../../constants/api/custom-pages-api.constants';
import {
    DefaultPageConfigurationType,
    DefaultPageType,
    ICustomPageConfigurationDTO,
    ICustomPagesDAO,
    ICustomPageTypesDTO,
    IMeetingPresenterGroupDTO,
    IReplaysPageLayoutOptionsDTO,
    ISessionsPageLayoutOptionsDTO,
    IUpcomingSessionsTypeOptionsDTO
} from '../custom-pages.dao';
import { IEventRequestParams, IAssetUrlDTO } from '../shared.dto';

export class CustomPagesDAO implements ICustomPagesDAO {
    public async getCustomPageTypes(params) {
        const response = await this.apiClient.get<ICustomPageTypesDTO>(URL.CUSTOM_PAGE_TYPES_URL, params);
        return response.parsedResponse;
    }

    public async getCustomPagesConfiguration(params): Promise<ICustomPageConfigurationDTO> {
        const response = await this.apiClient.get<ICustomPageConfigurationDTO>(URL.CUSTOM_PAGES_CONFIG_LIST_URL, params);
        return response.parsedResponse;
    }

    public async updateCustomPagesConfiguration(params, payload): Promise<ICustomPageConfigurationDTO> {
        const response = await this.apiClient.put<ICustomPageConfigurationDTO>(URL.CUSTOM_PAGES_CONFIG_URL, { ...params, ...payload });
        return response.parsedResponse;
    }

    public async getS3AssetUrl(params: IEventRequestParams): Promise<IAssetUrlDTO> {
        const response = await this.apiClient.get<IAssetUrlDTO>(URL.GET_FILE_S3_URL, params);
        return response.parsedResponse;
    }

    public async uploadFileToS3(file: File, params: IEventRequestParams): Promise<IAssetUrlDTO> {
        const fileUrlParams = await this.getS3AssetUrl(params);
        await this.apiClient.put(fileUrlParams.assetUrl, file, {
            ContentType: RequestContentType.html
        });
        return fileUrlParams;
    }

    public async getDefaultPageConfigByType(type, params): Promise<DefaultPageConfigurationType> {
        const queryParams = { uriParams: { type: this.defaultPageTypeMap[type] }};
        const response = await this.apiClient.get<DefaultPageConfigurationType>(URL.DEFAULT_PAGE_CONFIG_URL, params, queryParams);
        return response.parsedResponse;
    }

    public async updateDefaultPageConfig(payload, params): Promise<DefaultPageConfigurationType> {
        const queryParams = { uriParams: { type: this.defaultPageTypeMap[payload.type] }};
        const response = await this.apiClient.put<DefaultPageConfigurationType>(URL.DEFAULT_PAGE_CONFIG_URL, { ...payload, ...params }, queryParams);

        return response.parsedResponse;
    }

    public async getSessionsPageLayoutOptions(params) {
        const response = await this.apiClient.get<ISessionsPageLayoutOptionsDTO>(URL.PAGE_LAYOUT_OPTIONS_URL, params);
        return response.parsedResponse;
    }

    public async getUpcomingSessionsTypeOptions(params) {
        const response = await this.apiClient.get<IUpcomingSessionsTypeOptionsDTO>(URL.UPCOMING_SESSIONS_TYPES_URL, params);
        return response.parsedResponse;
    }

    public async getReplaysPageLayoutOptions(params) {
        const response = await this.apiClient.get<IReplaysPageLayoutOptionsDTO>(URL.PAGE_LAYOUT_OPTIONS_URL, params);
        return response.parsedResponse;
    }

    public async getMeetingPresenterGroups(params): Promise<IMeetingPresenterGroupDTO[]> {
        const response = await this.apiClient.get<IMeetingPresenterGroupDTO[]>(URL.MEETING_PRESENTERS_GROUPS_URL, params);
        return response.parsedResponse;
    }

    constructor(@IoC.Inject private readonly apiClient: IApiClient) {}

    private get defaultPageTypeMap(): Record<DefaultPageType, string> {
        return {
            [DefaultPageType.Agenda]: 'AGENDA',
            [DefaultPageType.Session]: 'SESSION',
            [DefaultPageType.Showcase]: 'SHOWCASE',
            [DefaultPageType.Speakers]: 'SPEAKERS',
            [DefaultPageType.Support]: 'SUPPORT',
            [DefaultPageType.Welcome]: 'WELCOME',
            [DefaultPageType.Replays]: 'REPLAYS'
        };
    }
}
